import * as React from 'react';
import { IAthlete, IGroup, ITeam } from '../../../data/types';
import { useListSearch } from '../../../hooks/useListSearch';
import { FullHeightColumn } from '../../layout/Column';
import { Row } from '../../layout/Row';
import { ScrollableArea } from '../../layout/ScrollableArea';
import { MultiAthleteSelectList } from '../../pages/dashboard/athletes/MultiAthleteSelectList';
import { MultiGroupSelectList } from '../../pages/dashboard/groups/MultiGroupSelectList';
import { ClearAllButton, SelectAllButton } from '../../shared/Button';
import { Count } from '../../shared/Count';
import { ListControlLabel } from '../../shared/form/ListControlLabel';
import { ListControlContainer } from '../../shared/List/ListControlContainer';

interface IGroupManagementProps {
  groups: IGroup[];
  selectedGroups: IGroup[];
  groupSelectionHandler: (group: IGroup, checked: boolean) => void;
  clearAllGroups: () => void;
  selectAllGroups: () => void;
}
interface IProps {
  team: Partial<ITeam>;
  teams: ITeam[];
  error?: string;
}

interface IAthleteManagementProps {
  athletes: IAthlete[];
  selectedAthletes: IAthlete[];
  athleteSelectionHandler: (athlete: IAthlete, checked: boolean) => void;
  clearAllAthletes: () => void;
  selectAllAthletes: () => void;
}

export const ArchiveForm = (
  props: IProps & IGroupManagementProps & IAthleteManagementProps
): React.ReactElement => {
  const {
    list: athletes,
    search: searchAthletes,
    clear: clearAthleteSearch,
    searchTerm: athleteSearchTerm,
  } = useListSearch(props.athletes);

  const {
    list: groups,
    search: searchGroups,
    clear: clearGroupSearch,
    searchTerm: groupSearchTerm,
  } = useListSearch(props.groups);

  return (
    <Row shouldCollapse>
      <FullHeightColumn>
        <ListControlLabel
          label='Select Group(s) (optional)'
          onSearchClearHandler={clearGroupSearch}
          searchValue={groupSearchTerm}
          onSearchHandler={searchGroups}
        >
          <ClearAllButton
            onClick={props.clearAllGroups}
            disabled={props.selectedGroups.length === 0}
          />
          <SelectAllButton
            onClick={props.selectAllGroups}
            disabled={props.selectedGroups.length === props.groups.length}
          />
          <Count value={props.selectedGroups.length} />
        </ListControlLabel>
        <ListControlContainer>
          <ScrollableArea
            style={{
              flex: 1,
            }}
          >
            <MultiGroupSelectList
              teams={props.teams}
              selectedTeams={props.teams}
              disabled={props.teams.length < 1}
              groups={groups}
              selectedGroups={props.selectedGroups}
              onSelect={props.groupSelectionHandler}
              emptyMessage={
                groupSearchTerm.length > 0
                  ? `No groups matching '${groupSearchTerm}'`
                  : 'There are no currently archived groups.'
              }
            />
          </ScrollableArea>
        </ListControlContainer>
      </FullHeightColumn>
      <FullHeightColumn>
        <ListControlLabel
          label='Select Athletes (optional)'
          onSearchClearHandler={clearAthleteSearch}
          searchValue={athleteSearchTerm}
          onSearchHandler={searchAthletes}
        >
          <ClearAllButton
            onClick={props.clearAllAthletes}
            disabled={props.selectedAthletes.length === 0}
          />
          <SelectAllButton
            onClick={props.selectAllAthletes}
            disabled={props.selectedAthletes.length === props.athletes.length}
          />
          <Count
            value={props.selectedAthletes.length}
            style={{
              alignSelf: 'flex-end',
            }}
          />
        </ListControlLabel>
        <ListControlContainer>
          <ScrollableArea
            style={{
              flex: 1,
            }}
          >
            <MultiAthleteSelectList
              showMergeOption
              athletes={athletes}
              teams={props.teams}
              selectedTeams={props.teams}
              selectedAthletes={props.selectedAthletes}
              onSelect={props.athleteSelectionHandler}
              clearSelected={props.clearAllAthletes}
              disabled={!Boolean(props.teams.length)}
              emptyMessage={
                athleteSearchTerm.length > 0
                  ? `No athletes matching '${athleteSearchTerm}'`
                  : 'There are no currently archived athletes.'
              }
            />
          </ScrollableArea>
        </ListControlContainer>
      </FullHeightColumn>
    </Row>
  );
};
